'use client';

import React from 'react';
import type { ReactNode } from 'react';
import type { MenuItemProps as AriaMenuItemProps, MenuItemRenderProps } from 'react-aria-components';
import { MenuItem as AriaMenuItem } from 'react-aria-components';
import { cx } from '../../../utils/cx';
import { MenuItemBase as MenuItemRootBase } from './MenuItemBase';
import { styles } from './menuItemStyles';
import { useMenuItemAction } from './useMenuItemAction';

const MENU_ITEM_DISPLAY_NAME = 'RomaMenuItem';

export type CustomMenuItemProps<T extends object> = Omit<
    AriaMenuItemProps<T>,
    'children' | 'className' | 'textValue'
> & {
    className?: AriaMenuItemProps<T>['className'];
    textValue: string;
    children: ReactNode | ((state: MenuItemRenderProps) => ReactNode);
    /** Leaves the menu open after the item is actioned, so a toggle can be flipped repeatedly. */
    keepOpenOnSelect?: boolean;
};

/**
 * Renders a menu item with fully custom row content while preserving shared item behavior.
 */
export const CustomMenuItem = <T extends object>(props: CustomMenuItemProps<T>) => {
    const { className, textValue, children, keepOpenOnSelect, onAction, onPress, onPressStart, ...ariaProps } = props;
    const actionHandlers = useMenuItemAction({ onAction, onPress, onPressStart });

    return (
        <AriaMenuItem
            {...ariaProps}
            {...actionHandlers}
            {...(keepOpenOnSelect && { shouldCloseOnSelect: false })}
            textValue={textValue}
            className={(state) =>
                cx(
                    MENU_ITEM_DISPLAY_NAME,
                    styles.common.menuItem,
                    typeof className === 'function' ? className(state) : className,
                )
            }
        >
            {(state) => {
                const rootClassName = state.isDisabled ? styles.variants.disabled.root : styles.variants.default.root;

                return (
                    <MenuItemRootBase
                        className={rootClassName}
                        isSelected={state.isSelected}
                        isDisabled={state.isDisabled}
                        isFocused={state.isFocused}
                        isFocusVisible={state.isFocusVisible}
                        isHovered={state.isHovered}
                    >
                        <div className={styles.common.row}>
                            {typeof children === 'function' ? children(state) : children}
                        </div>
                    </MenuItemRootBase>
                );
            }}
        </AriaMenuItem>
    );
};

CustomMenuItem.displayName = MENU_ITEM_DISPLAY_NAME;
